import { useQuery } from '@tanstack/react-query'
import type { ResolvedFlagsResponseDto } from '@fp3/shared'
import { featureFlagsApi } from '../api/featureFlagsApi'

export const useFeatureFlags = () => {
  return useQuery<ResolvedFlagsResponseDto>({
    queryKey: ['feature-flags', 'resolved'],
    queryFn: featureFlagsApi.resolve,
    staleTime: 5 * 60_000,
  })
}

export interface FlagState {
  enabled: boolean
  // no answer yet, but one is on its way. `enabled` is meaningless until this is false —
  // a caller that gates a redirect on the flag MUST wait, or it reads "not resolved yet"
  // as "disabled".
  isPending: boolean
  // no answer is coming: the first load failed outright, or the browser is offline and
  // query-core paused the request rather than erroring. `enabled` is meaningless here too,
  // and unlike isPending, waiting does not help.
  isUnresolvable: boolean
  error: Error | null
  // a retry is in flight; nothing else about the state changes while it runs
  isRetrying: boolean
  retry: () => void
}

export const useFlag = (name: string): FlagState => {
  const { data, error, isPaused, isLoadingError, isFetching, refetch } = useFeatureFlags()

  // once the flags have resolved even once they stay authoritative. query-core keeps the
  // data through a later failure — it only marks it invalidated — so a background refetch
  // that fails (a token-refresh window, a reconnect) must not evict an already-granted
  // flag. that makes "do we have data" the resolution test, not the query's status.
  const resolved = data !== undefined

  return {
    enabled: data?.[name]?.enabled ?? false,
    // deliberately not TanStack's `isLoading` (= isPending && isFetching), which reads
    // false in the window PersistQueryClientProvider holds queries back while it restores
    // the persisted cache: there the query is pending with fetchStatus 'idle' and no data,
    // which is exactly when a cold page load would otherwise see every flag as disabled.
    isPending: !resolved && !isLoadingError && !isPaused,
    isUnresolvable: !resolved && (isLoadingError || isPaused),
    error,
    isRetrying: isFetching,
    retry: () => void refetch(),
  }
}

export const useFlagValue = (name: string): string | undefined => {
  const { data } = useFeatureFlags()
  return data?.[name]?.value
}
